import { useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

type ToastType = 'info' | 'success' | 'error'

interface ToastItem {
  id: number
  type: ToastType
  message: string
}

let _addToast: ((type: ToastType, message: string) => void) | null = null
let _counter = 0

export function showToast(type: ToastType, message: string) {
  if (_addToast) {
    _addToast(type, message)
  }
}

const typeStyles: Record<ToastType, { bg: string; border: string; icon: string }> = {
  info: { bg: '#1A1A38', border: '#1E4ACC', icon: 'ℹ' },
  success: { bg: '#1A1A38', border: '#4CAF8A', icon: '✓' },
  error: { bg: '#1A1A38', border: '#E85D5D', icon: '✕' },
}

const typeIconColors: Record<ToastType, string> = {
  info: '#5B8CFF',
  success: '#4CAF8A',
  error: '#E85D5D',
}

function ToastItemEl({ item, onRemove }: { item: ToastItem; onRemove: (id: number) => void }) {
  const style = typeStyles[item.type]

  useEffect(() => {
    const timer = setTimeout(() => onRemove(item.id), 2500)
    return () => clearTimeout(timer)
  }, [item.id, onRemove])

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg animate-slide-down"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        minWidth: 280,
        maxWidth: 360,
      }}
    >
      <span
        className="text-base font-bold shrink-0 w-5 h-5 flex items-center justify-center rounded-full"
        style={{ color: typeIconColors[item.type] }}
      >
        {style.icon}
      </span>
      <span className="text-text text-sm">{item.message}</span>
    </div>
  )
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const addRef = useRef<(type: ToastType, message: string) => void>()

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  addRef.current = useCallback((type: ToastType, message: string) => {
    const id = ++_counter
    setToasts((prev) => [...prev, { id, type, message }])
  }, [])

  useEffect(() => {
    _addToast = (type, message) => addRef.current?.(type, message)
    return () => {
      _addToast = null
    }
  }, [])

  if (toasts.length === 0) return null

  return createPortal(
    <div
      className="fixed top-4 left-0 right-0 z-[1000] flex flex-col items-center gap-2 pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastItemEl key={t.id} item={t} onRemove={remove} />
      ))}
    </div>,
    document.body
  )
}

// Convenience helpers
export const showSuccess = (message: string) => showToast('success', message)
export const showError = (message: string) => showToast('error', message)
export const showInfo = (message: string) => showToast('info', message)

// Legacy compatibility (no-op stubs for old code that uses showLoading/dismissToast)
export const showLoading = (_message: string): number => 0
export const dismissToast = (_id?: string | number) => {}
